import EditIcon from '@mui/icons-material/Edit';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import GenerateInvoice from '../Invoice/GenerateInvoice';
import BolPrint from '../Print/BolPrint';

type Props = {
  data: any[];
  onEdit?: (row: any) => void;
  rowActions?: (row: any) => React.ReactNode;
  getCardSx?: (row: any) => Record<string, any>;
};

export default function BolMobileList({
  data,
  onEdit,
  rowActions,
  getCardSx,
}: Props) {
  const renderStatusChip = (
    value: boolean,
    trueLabel: string,
    falseLabel: string,
    trueColor: 'success' | 'info' | 'warning' | 'error' = 'success',
    falseColor: 'success' | 'info' | 'warning' | 'error' = 'error'
  ) => (
    <Chip
      size="small"
      label={value ? trueLabel : falseLabel}
      color={value ? trueColor : falseColor}
      variant={value ? 'filled' : 'outlined'}
    />
  );

  return (
    <Stack spacing={1.5}>
      {data.map((row) => {
        const receiverAddress = [
          row?.receiver?.address,
          row?.receiver?.postal_code,
        ]
          .filter(Boolean)
          .join(', ');

        const shipperAddress = [
          row?.shipper?.address,
          row?.shipper?.postal_code,
        ]
          .filter(Boolean)
          .join(', ');
        return (
          <Card
            key={row.bol_no}
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              ...(row.cancel && {
                backgroundColor: '#f5f5f5',
                '& *': { color: '#8c8c8c' },
              }),
              ...(getCardSx ? getCardSx(row) : {}),
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                {/* Header */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={700} noWrap>
                      <BolPrint
                        bol={row}
                        variant="outline"
                        textElement={row.bol_no}
                      />
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      {row?.bol_date
                        ? String(row.bol_date).split('T')[0]
                        : 'No date'}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {renderStatusChip(
                      !!row?.payment_status,
                      'Paid',
                      'Unpaid',
                      'success',
                      'error'
                    )}

                    {onEdit && (
                      <EditIcon
                        onClick={() => onEdit(row)}
                        fontSize="small"
                        sx={{ cursor: 'pointer' }}
                      />
                    )}
                  </Stack>
                </Stack>

                {/* Info */}
                <Stack direction="row" justifyContent="space-between">
                  <Stack spacing={0.5}>
                    <Typography variant="body2">
                      <strong>Shipper:</strong> {row?.shipper?.name || 'N/A'}
                    </Typography>

                    <Typography variant="body2">
                      <strong>Receiver:</strong> {row?.receiver?.name || 'N/A'}
                    </Typography>

                    <Typography variant="body2">
                      <strong>Carrier:</strong> {row?.trailer || 'N/A'}
                    </Typography>

                    <Typography variant="body2">
                      <strong>Amount:</strong>{' '}
                      {row?.amount ? `$${row.amount}` : 'N/A'}
                    </Typography>
                  </Stack>

                  <Stack spacing={0.5}>

                    {/* Address */}
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        px: 1.25,
                        py: 1,
                      }}
                    >
                      <LocationOnOutlinedIcon fontSize="small" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Receiver Address
                        </Typography>
                        <Typography variant="body2">
                          {receiverAddress || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    <Divider />
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        px: 1.25,
                        py: 1,
                      }}
                    >
                      <LocationOnOutlinedIcon fontSize="small" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Shipper Address
                        </Typography>
                        <Typography variant="body2">
                          {shipperAddress || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>

                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">

                  <Typography variant="body2">
                    {row?.invoice_no ? (
                      <Stack
                        direction="row"
                        sx={{
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        #
                        <GenerateInvoice
                          bol={row}
                          textElement={row.invoice_no}
                        />
                      </Stack>
                    ) : (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <ReceiptLongIcon fontSize="small" />
                        <Typography variant="body2">
                          No Invoice
                        </Typography>
                      </Stack>
                    )}
                  </Typography>

                  {/* Extra statuses */}
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {renderStatusChip(
                      !!row?.dc_received,
                      'DC-Reveived',
                      'DC - Pending',
                      'info'
                    )}
                    {renderStatusChip(
                      !!row?.vendor_delivered,
                      'Delivered',
                      'Vendor-Pending',
                      'success'
                    )}
                  </Stack>
                </Stack>

                {/* Actions */}
                {rowActions && (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {rowActions(row)}
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
}